/**
 * Campaigns (#3034): a named, dated set of special prices (a fixed net price or a percent discount) per part or
 * category. A campaign lasts at most 366 days and holds at most 500 items. The accepted API has no bulk replace, so
 * items are managed one at a time (add, change value, remove), each an explicit action, and the items list pages with an
 * explicit "load more". "Active today" is the server's, from the tenant's own date.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/stores/notificationStore';
import {
  flattenPages,
  useAddCampaignItem,
  useCampaign,
  useCampaigns,
  useCreateCampaign,
  useDeleteCampaign,
  useDeleteCampaignItem,
  useKeyedSubmit,
  usePricingPermissions,
  useUpdateCampaign,
  useUpdateCampaignItem,
  type Campaign,
  type CampaignItem,
  type CampaignItemBody,
  type CampaignItemSaved,
  type CreateCampaignBody,
  type UpdateCampaignBody,
  type UpdateCampaignItemBody,
} from '@/hooks/usePartPricing';
import {
  MAX_CAMPAIGN_DAYS,
  MAX_CAMPAIGN_ITEMS,
  cents4ToEurosInput,
  checkWindow,
  formatDate,
  normalizeCents4,
  percentToInput,
} from './decimal';
import { JournalStatusNotice, PricingErrorAlert, UncertainOutcomeAlert } from './feedback';
import { OwnedBoundary } from './OwnedBoundary';
import { getJournalView, useJournalView } from './pricingJournal';
import { MANAGE_PERMISSIONS, isSessionCancel, readPricingAccess, usePricingAccess } from './pricingSession';
import { describePricingError } from './pricingErrors';
import { CategoryName, PartName } from './pickers';
import {
  PricingWarnings,
  RuleValueFields,
  TargetFields,
  WindowFields,
  emptyTarget,
  parseRuleValue,
  targetBody,
  useRuleValueText,
  type RuleValueDraft,
  type TargetDraft,
} from './ruleForm';

const NAME_MAX = 100;

function CampaignState({ campaign }: { campaign: Campaign }) {
  const { t } = useTranslation('partPricing');
  if (!campaign.isActive) return <Chip size="small" label={t('state.disabled')} />;
  return campaign.activeToday ? (
    <Chip size="small" color="success" label={t('state.activeToday')} />
  ) : (
    <Chip size="small" color="warning" label={t('state.notActiveToday')} />
  );
}

// ---------------------------------------------------------------------------
// Campaign header dialog (create / edit)
// ---------------------------------------------------------------------------

function CampaignDialog({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const createSubmit = useKeyedSubmit<CreateCampaignBody, Awaited<ReturnType<typeof create.mutateAsync>>>(create.mutateAsync, {
    journal: { kind: 'campaign_create', target: null },
    permissions: MANAGE_PERMISSIONS,
  });
  const [name, setName] = useState(campaign?.name ?? '');
  const [period, setPeriod] = useState({ from: campaign?.startsOn ?? '', to: campaign?.endsOn ?? '' });
  const [isActive, setIsActive] = useState(campaign?.isActive ?? true);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const held = createSubmit.held !== null;
  const damaged = createSubmit.journalStatus === 'corrupt';
  const busy = update.isPending || createSubmit.isPending;
  const locked = busy || held || damaged;
  const trimmed = name.trim();
  const nameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;
  const windowFailure = checkWindow(period.from, period.to, { allowOpen: false, maxDays: MAX_CAMPAIGN_DAYS });
  const valid = nameValid && windowFailure === null;

  const done = () => {
    notify.success(t('campaigns.saved'));
    onClose();
  };

  const save = async () => {
    setShowErrors(true);
    if (!valid) return;
    setError(null);
    if (campaign) {
      const body: UpdateCampaignBody = {};
      if (trimmed !== campaign.name) body.name = trimmed;
      if (period.from !== campaign.startsOn) body.startsOn = period.from;
      if (period.to !== campaign.endsOn) body.endsOn = period.to;
      if (isActive !== campaign.isActive) body.isActive = isActive;
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      try {
        await update.mutateAsync({ id: campaign.id, body });
        done();
      } catch (failure) {
        if (isSessionCancel(failure)) return;
        setError(failure);
      }
      return;
    }
    const result = await createSubmit.submit({ name: trimmed, startsOn: period.from, endsOn: period.to, isActive });
    if (result.status === 'success') done();
    else if (result.status === 'refused') setError(result.error);
  };

  const retry = async () => {
    const result = await createSubmit.retry();
    if (result.status === 'success') done();
    else if (result.status === 'refused') setError(result.error);
  };

  return (
    <ResponsiveDialog
      open
      onClose={() => {
        if (!locked) onClose();
      }}
      maxWidth="sm"
      fullWidth
      mobileTitle={campaign ? t('campaigns.editTitle') : t('campaigns.addTitle')}
    >
      <DialogTitle>{campaign ? t('campaigns.editTitle') : t('campaigns.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            required
            label={t('fields.name')}
            value={name}
            disabled={locked}
            onChange={(event) => setName(event.target.value)}
            error={showErrors && !nameValid}
            helperText={showErrors && !nameValid ? t('validation.name.invalid', { max: NAME_MAX }) : undefined}
          />
          <WindowFields
            from={period.from}
            to={period.to}
            onChange={setPeriod}
            allowOpen={false}
            maxDays={MAX_CAMPAIGN_DAYS}
            fromLabel={t('fields.startsOn')}
            toLabel={t('fields.endsOn')}
            disabled={locked}
            showErrors={showErrors}
          />
          <FormControlLabel
            control={<Switch checked={isActive} disabled={locked} onChange={(event) => setIsActive(event.target.checked)} />}
            label={t('fields.campaignActive')}
          />
          {createSubmit.held && (
            <UncertainOutcomeAlert
              what={t('campaigns.uncertainWhat')}
              error={createSubmit.error}
              attempts={createSubmit.attempts}
              busy={createSubmit.isPending}
              onRetry={() => void retry()}
              onDiscard={createSubmit.discard}
              journalStatus={createSubmit.journalStatus}
            />
          )}
          <JournalStatusNotice status={createSubmit.journalStatus} holding={held} onDiscardDamaged={createSubmit.discardDamaged} />
          <PricingErrorAlert error={held ? null : error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={locked}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={locked}>
          {busy ? tc('saving') : tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------------------
// Item dialog (add / edit)
// ---------------------------------------------------------------------------

function CampaignItemDialog({ campaignId, item, onClose }: { campaignId: string; item: CampaignItem | null; onClose: () => void }) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const add = useAddCampaignItem(campaignId);
  const update = useUpdateCampaignItem(campaignId);
  // The request keeps ITS campaign: the slot is per campaign, so another campaign neither sees nor resends it.
  const addSubmit = useKeyedSubmit<CampaignItemBody, CampaignItemSaved>(add.mutateAsync, {
    journal: { kind: 'campaign_item_add', target: campaignId },
    permissions: MANAGE_PERMISSIONS,
  });
  const [target, setTarget] = useState<TargetDraft>(emptyTarget);
  const [valueDraft, setValueDraft] = useState<RuleValueDraft>(
    item
      ? { priceType: item.priceType, valueText: item.priceType === 'fixed_price' ? cents4ToEurosInput(item.value) : percentToInput(item.value) }
      : { priceType: 'fixed_price', valueText: '' },
  );
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [savedWarnings, setSavedWarnings] = useState<CampaignItemSaved['warnings'] | null>(null);

  const held = addSubmit.held !== null;
  const damaged = addSubmit.journalStatus === 'corrupt';
  const busy = update.isPending || addSubmit.isPending;
  const locked = busy || held || damaged || savedWarnings !== null;
  const value = parseRuleValue(valueDraft);
  const targetValue = item ? null : targetBody(target);

  const finish = (saved: CampaignItemSaved) => {
    notify.success(t('campaigns.itemSaved'));
    if (saved.warnings.length > 0) setSavedWarnings(saved.warnings);
    else onClose();
  };

  const save = async () => {
    setShowErrors(true);
    if (!value.ok || (!item && !targetValue)) return;
    setError(null);
    if (item) {
      const body: UpdateCampaignItemBody = {};
      if (valueDraft.priceType !== item.priceType || value.value !== normalizeCents4(item.value)) {
        body.priceType = valueDraft.priceType;
        body.value = value.value;
      }
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      try {
        finish(await update.mutateAsync({ itemId: item.id, body }));
      } catch (failure) {
        if (isSessionCancel(failure)) return;
        setError(failure);
      }
      return;
    }
    if (!targetValue) return;
    const result = await addSubmit.submit({ ...targetValue, priceType: valueDraft.priceType, value: value.value } as CampaignItemBody);
    if (result.status === 'success') finish(result.result);
    else if (result.status === 'refused') setError(result.error);
  };

  const retry = async () => {
    const result = await addSubmit.retry();
    if (result.status === 'success') finish(result.result);
    else if (result.status === 'refused') setError(result.error);
  };

  return (
    <ResponsiveDialog
      open
      onClose={() => {
        if (!busy && !held) onClose();
      }}
      maxWidth="sm"
      fullWidth
      mobileTitle={item ? t('campaigns.editItemTitle') : t('campaigns.addItemTitle')}
    >
      <DialogTitle>{item ? t('campaigns.editItemTitle') : t('campaigns.addItemTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {item ? (
            <Typography variant="body1">{item.partId ? <PartName id={item.partId} /> : <CategoryName id={item.categoryId} />}</Typography>
          ) : (
            <TargetFields draft={target} onChange={setTarget} disabled={locked} showErrors={showErrors} />
          )}
          <RuleValueFields draft={valueDraft} onChange={setValueDraft} disabled={locked} showErrors={showErrors} />
          {savedWarnings && <PricingWarnings warnings={savedWarnings} />}
          {addSubmit.held && (
            <UncertainOutcomeAlert
              what={t('campaigns.uncertainItemWhat')}
              error={addSubmit.error}
              attempts={addSubmit.attempts}
              busy={addSubmit.isPending}
              onRetry={() => void retry()}
              onDiscard={addSubmit.discard}
              journalStatus={addSubmit.journalStatus}
            />
          )}
          <JournalStatusNotice status={addSubmit.journalStatus} holding={held} onDiscardDamaged={addSubmit.discardDamaged} />
          <PricingErrorAlert error={held ? null : error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        {savedWarnings ? (
          <Button variant="contained" onClick={onClose}>
            {tc('close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy || held}>
              {tc('cancel')}
            </Button>
            <Button variant="contained" onClick={() => void save()} disabled={busy || held || damaged}>
              {busy ? tc('saving') : tc('save')}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------------------
// Campaign detail: items
// ---------------------------------------------------------------------------

function CampaignDetailDialog({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { canManage } = usePricingPermissions();
  const ruleValueText = useRuleValueText();
  const detail = useCampaign(campaignId);
  const removeItem = useDeleteCampaignItem(campaignId);
  const [itemDialog, setItemDialog] = useState<{ item: CampaignItem | null } | null>(null);
  const [deleting, setDeleting] = useState<CampaignItem | null>(null);
  // An item add of THIS campaign whose outcome is unknown reopens its dialog (also after a reload); another campaign's does not.
  const { owner } = usePricingAccess();
  const unresolvedItem = useJournalView(owner).entries.some((entry) => entry.slot === `campaign_item_add:${campaignId}`);
  const shownItemDialog = itemDialog ?? (unresolvedItem && canManage ? { item: null } : null);

  const pages = detail.data?.pages ?? [];
  const campaign = pages[pages.length - 1]?.campaign;
  const items = pages.flatMap((page) => page.items.data);
  const total = pages[pages.length - 1]?.items.pagination.total ?? 0;
  const full = total >= MAX_CAMPAIGN_ITEMS;

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    try {
      await removeItem.mutateAsync(target.id);
      notify.success(t('campaigns.itemDeleted'));
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      notify.error(t('campaigns.itemDeleteFailed'), describePricingError(failure, t, tc));
    } finally {
      setDeleting((current) => (current?.id === target.id ? null : current));
    }
  };

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="md" fullWidth mobileTitle={campaign?.name ?? t('campaigns.title')}>
      <DialogTitle>{campaign?.name ?? t('campaigns.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {campaign && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                {t('rule.window', { from: formatDate(campaign.startsOn, i18n.language), to: formatDate(campaign.endsOn, i18n.language) })}
              </Typography>
              <CampaignState campaign={campaign} />
            </Stack>
          )}
          {detail.isError && <PricingErrorAlert error={detail.error} />}
          {detail.isLoading && <Typography color="text.secondary">{tc('loading')}</Typography>}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1">{t('campaigns.itemsTitle', { count: total, max: MAX_CAMPAIGN_ITEMS })}</Typography>
            {canManage && (
              <Button startIcon={<AddIcon />} variant="outlined" disabled={full} onClick={() => setItemDialog({ item: null })}>
                {t('campaigns.addItem')}
              </Button>
            )}
          </Stack>
          {full && <Alert severity="info">{t('campaigns.itemsFull', { max: MAX_CAMPAIGN_ITEMS })}</Alert>}
          {!detail.isLoading && !detail.isError && items.length === 0 && <Typography color="text.secondary">{t('campaigns.noItems')}</Typography>}
          {items.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label={t('campaigns.itemsLabel')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('fields.target')}</TableCell>
                    <TableCell>{t('fields.price')}</TableCell>
                    {canManage && <TableCell align="right">{tc('actions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {item.partId ? <PartName id={item.partId} /> : <CategoryName id={item.categoryId} />}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t(item.partId ? 'targetKind.part' : 'targetKind.category')}
                        </Typography>
                      </TableCell>
                      <TableCell>{ruleValueText(item.priceType, item.value)}</TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <Tooltip title={tc('edit')}>
                            <IconButton size="small" aria-label={tc('edit')} onClick={() => setItemDialog({ item })}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tc('delete')}>
                            <IconButton size="small" aria-label={tc('delete')} onClick={() => setDeleting(item)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {items.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="caption" color="text.secondary">
                {t('list.showing', { shown: items.length, total })}
              </Typography>
              {detail.hasNextPage && (
                <Button size="small" onClick={() => void detail.fetchNextPage()} disabled={detail.isFetchingNextPage}>
                  {detail.isFetchingNextPage ? tc('loading') : tc('loadMore')}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
      {shownItemDialog && (
        <CampaignItemDialog key={shownItemDialog.item?.id ?? 'new'} campaignId={campaignId} item={shownItemDialog.item} onClose={() => setItemDialog(null)} />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title={t('campaigns.deleteItemTitle')}
        message={t('campaigns.deleteItemMessage')}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={removeItem.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!removeItem.isPending) setDeleting(null);
        }}
      />
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

function CampaignsPanelBody() {
  const { t, i18n } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { canManage } = usePricingPermissions();
  const remove = useDeleteCampaign();
  const [stateFilter, setStateFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const campaigns = useCampaigns({ isActive: stateFilter === 'all' ? undefined : stateFilter === 'enabled' });
  const rows = flattenPages(campaigns.data);
  const total = campaigns.data?.pages[campaigns.data.pages.length - 1]?.pagination.total ?? 0;
  const [dialog, setDialog] = useState<{ campaign: Campaign | null } | null>(null);
  // The detail of a campaign with an item add of unknown outcome opens once on mount (the user may close it; the notice above stays).
  const [openId, setOpenId] = useState<string | null>(() => {
    const live = readPricingAccess();
    const pending = getJournalView(live.owner).entries.find((entry) => entry.kind === 'campaign_item_add');
    return live.canRead && pending ? pending.target : null;
  });
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const { owner } = usePricingAccess();
  const unresolvedCreate = useJournalView(owner).entries.some((entry) => entry.slot === 'campaign_create');
  const shownDialog = dialog ?? (unresolvedCreate && canManage ? { campaign: null } : null);

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    try {
      await remove.mutateAsync(target.id);
      notify.success(t('campaigns.deleted'));
    } catch (failure) {
      if (isSessionCancel(failure)) return;
      notify.error(t('campaigns.deleteFailed'), describePricingError(failure, t, tc));
    } finally {
      setDeleting((current) => (current?.id === target.id ? null : current));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6">{t('campaigns.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('campaigns.intro', { days: MAX_CAMPAIGN_DAYS, items: MAX_CAMPAIGN_ITEMS })}
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ campaign: null })}>
            {t('campaigns.add')}
          </Button>
        )}
      </Stack>
      <Alert severity="info">{t('hints.cheapestWins')}</Alert>
      <TextField
        select
        size="small"
        label={t('fields.state')}
        value={stateFilter}
        onChange={(event) => setStateFilter(event.target.value as 'all' | 'enabled' | 'disabled')}
        sx={{ maxWidth: 240 }}
      >
        <MenuItem value="all">{tc('all')}</MenuItem>
        <MenuItem value="enabled">{t('state.enabledFilter')}</MenuItem>
        <MenuItem value="disabled">{t('state.disabled')}</MenuItem>
      </TextField>

      {campaigns.isError && <PricingErrorAlert error={campaigns.error} />}
      {campaigns.isLoading && <Typography color="text.secondary">{tc('loading')}</Typography>}
      {!campaigns.isLoading && !campaigns.isError && rows.length === 0 && <Typography color="text.secondary">{t('campaigns.empty')}</Typography>}
      {rows.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label={t('campaigns.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('fields.name')}</TableCell>
                <TableCell>{t('fields.validity')}</TableCell>
                <TableCell align="right">{t('fields.items')}</TableCell>
                <TableCell>{t('fields.state')}</TableCell>
                <TableCell align="right">{tc('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{t('rule.window', { from: formatDate(row.startsOn, i18n.language), to: formatDate(row.endsOn, i18n.language) })}</TableCell>
                  <TableCell align="right">{row.itemCount ?? 0}</TableCell>
                  <TableCell>
                    <CampaignState campaign={row} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('campaigns.openItems')}>
                      <IconButton size="small" aria-label={`${t('campaigns.openItems')}: ${row.name}`} onClick={() => setOpenId(row.id)}>
                        <ListAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <>
                        <Tooltip title={tc('edit')}>
                          <IconButton size="small" aria-label={`${tc('edit')}: ${row.name}`} onClick={() => setDialog({ campaign: row })}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tc('delete')}>
                          <IconButton size="small" aria-label={`${tc('delete')}: ${row.name}`} onClick={() => setDeleting(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {t('list.showing', { shown: rows.length, total })}
          </Typography>
          {campaigns.hasNextPage && (
            <Button size="small" onClick={() => void campaigns.fetchNextPage()} disabled={campaigns.isFetchingNextPage}>
              {campaigns.isFetchingNextPage ? tc('loading') : tc('loadMore')}
            </Button>
          )}
        </Stack>
      )}

      {shownDialog && <CampaignDialog key={shownDialog.campaign?.id ?? 'new'} campaign={shownDialog.campaign} onClose={() => setDialog(null)} />}
      {openId && <CampaignDetailDialog campaignId={openId} onClose={() => setOpenId(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        title={t('campaigns.deleteTitle')}
        message={t('campaigns.deleteMessage', { name: deleting?.name ?? '' })}
        confirmText={tc('delete')}
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!remove.isPending) setDeleting(null);
        }}
      />
    </Stack>
  );
}

export function CampaignsPanel() {
  return (
    <OwnedBoundary>
      <CampaignsPanelBody />
    </OwnedBoundary>
  );
}
